import { useEffect, useState } from 'react';
import { useDialog } from '../lib/useDialog';
import { Medal, PartyPopper, Sparkles, Star, Trophy, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Avatar } from './Avatar';

type Announcement = { month: string; winner_user_id: string | null; winner_name: string | null; highlight_text: string | null };

// Papelillos: íconos y colores que rotan (antes eran emojis).
const CONFETTI_ICONS = [PartyPopper, Star, Sparkles, Trophy, Medal];
const CONFETTI_COLORS = ['#f59e0b', '#2563eb', '#16a34a', '#e11d48', '#a855f7', '#0ea5e9', '#eab308'];

const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Anuncio del Employee of the Month (pedido 2026-08-03).
 *
 * Antes era un modal que se abría solo encima de la pantalla. Ahora es una BARRA AZUL arriba de
 * toda la app —igual que la de alertas urgentes, pero azul porque esto es una buena noticia— y la
 * celebración con papelillos sale recién cuando le hacen click.
 *
 * El cambio no es cosmético: el modal automático caía en medio de lo que la persona estaba
 * haciendo y se cerraba de un click para volver al trabajo, que es justo lo contrario de celebrar
 * algo. La barra espera a que tenga un segundo.
 *
 * "Visto" se marca igual que antes (una vez por usuario), celebre o cierre la barra.
 */
export function EomAnnouncementModal() {
  const { t, lang } = useI18n();
  const [a, setA] = useState<Announcement | null>(null);
  const [open, setOpen] = useState(false);

  /**
   * `seen` se define ANTES del hook, y no después, a propósito.
   *
   * Estaba declarada más abajo y `useDialog(() => seen())` la leía por closure. En los renders sin
   * anuncio el componente corta en el `return null` de abajo y esa línea NUNCA corre: el closure
   * quedaba apuntando a un `const` sin inicializar, y apretar Escape en cualquier pantalla tiraba
   * `Cannot access 'seen' before initialization` (Sentry 2026-09-16).
   *
   * Por eso lee `a` de la variable de estado en vez de recibirlo: sirve en cualquier render, y si
   * todavía no hay anuncio no hace nada en lugar de reventar.
   */
  const seen = () => {
    const m = a?.month;
    setA(null); setOpen(false);
    if (m) api.post('/api/evaluation/announcement/seen', { month: m }).catch(() => {});
  };
  const dialogo = useDialog(seen);

  useEffect(() => {
    api.get<{ announcement: Announcement | null }>('/api/evaluation/announcement').then((r) => setA(r.announcement)).catch(() => {});
  }, []);
  if (!a) return null;

  const monthLabel = (() => { const [y, mm] = a.month.split('-').map(Number); return new Date(y || 2000, (mm || 1) - 1, 1).toLocaleDateString(lang === 'es' ? 'es' : 'en-US', { month: 'long', year: 'numeric' }); })();
  const animate = !reduceMotion();

  if (!open) {
    return (
      <div className="eom-banner" role="status">
        <button className="eom-banner-main" onClick={() => setOpen(true)}>
          <span className="eom-banner-tag"><Trophy size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('eom.employeeOfTheMonth')}</span>
          <span className="eom-banner-msg">{t('eom.bannerCta', { name: a.winner_name || '—', month: monthLabel })}</span>
          <span className="eom-banner-go">{t('eom.bannerOpen')} →</span>
        </button>
        <button className="eom-banner-x" onClick={seen} title={t('ia.dismiss')}><X size={14} strokeWidth={1.75} /></button>
      </div>
    );
  }

  return (
    <div onClick={seen} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      {animate && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {/* Papelillos: más piezas y arranques escalonados para que la caída se vea llena en los
              primeros segundos, que es cuando la persona está mirando. */}
          {Array.from({ length: 70 }).map((_, i) => {
            const Ico = CONFETTI_ICONS[i % CONFETTI_ICONS.length];
            return (
              <span key={i} style={{
                position: 'absolute', top: '-10px', left: `${(i * 1.43) % 100}%`,
                color: CONFETTI_COLORS[i % CONFETTI_COLORS.length], display: 'inline-flex',
                animation: `eomfall ${2.4 + (i % 6) * 0.55}s linear ${(i % 11) * 0.22}s infinite`,
              }}><Ico size={11 + (i % 6) * 4} strokeWidth={1.75} /></span>
            );
          })}
        </div>
      )}
      <div ref={dialogo.ref} {...dialogo.props} onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 460, width: '100%', textAlign: 'center', position: 'relative',
        // Un highlight largo hacía el cuadro MÁS ALTO que la pantalla: centrado y sin scroll, se cortaba
        // arriba y abajo y el botón "Celebrate" quedaba fuera de alcance (Manuel 2026-10-02). Tope a la
        // pantalla y scroll por dentro, sin que la rueda se escape a la página de atrás.
        maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto', overscrollBehavior: 'contain', animation: animate ? 'eompop 0.5s cubic-bezier(.2,1.4,.4,1)' : undefined }}>
        <div style={{ color: 'var(--orange)', animation: animate ? 'eomtrophy 1.8s ease-in-out infinite' : undefined }}><Trophy size={56} strokeWidth={1.75} /></div>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}><Avatar userId={a.winner_user_id} name={a.winner_name} size={80} /></div>
        <div style={{ fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)' }}>{t('eom.congratsTitle')} · {monthLabel}</div>
        <h2 style={{ margin: '6px 0 4px', fontSize: 'var(--fs-xl)' }}>{a.winner_name || '—'}</h2>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--muted)' }}><Trophy size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('eom.employeeOfTheMonth')}</div>
        {a.highlight_text && <div style={{ marginTop: 12, fontSize: 'var(--fs-sm)', background: 'var(--surface-2)', borderRadius: 10, padding: '10px 12px' }}>“{a.highlight_text}”</div>}
        {/* El agradecimiento de la empresa: es la parte que la persona se queda leyendo. */}
        <div style={{ marginTop: 12, fontSize: 'var(--fs-sm)', lineHeight: 1.6, textAlign: 'left', borderLeft: '3px solid var(--info)', paddingLeft: 12 }}>
          {t('eom.thanksBody', { name: (a.winner_name || '').split(' ')[0] || '' })}
        </div>
        <button className="btn" style={{ marginTop: 16 }} onClick={seen}><PartyPopper size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('eom.celebrate')}</button>
      </div>
      <style>{`@keyframes eomfall{to{transform:translateY(110vh) rotate(360deg)}}@keyframes eompop{from{transform:scale(.7);opacity:0}to{transform:scale(1);opacity:1}}@keyframes eomtrophy{0%,100%{transform:rotate(-7deg)}50%{transform:rotate(7deg)}}`}</style>
    </div>
  );
}
